import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuthStore } from "../../../../store/storeAuth";
import { useDarkMode } from "../../../../hooks/useDarkMode";
import { UserService } from "../../../../service/UserService";
import { FlowService } from "../../../../service/FlowService";
import { FolderService } from "../../../../service/FolderService";
import { FormsService } from "../../../../service/FormService";
import { CompanySpacesService } from "../../../../service/CompanySpacesService";
import { useSidebarData } from "../../../../hooks/useSidebarData";
import { useObjectStore } from "../../../../store/storeObjects";
import useFlowsStore from "../../../../store/storeFlows";
import { useFormTabStore } from "../../../../store/storeFormTabs";
import {
  useNotificationStore,
  NotificationType,
} from "../../../../store/storeNotifications";
import { getAPIErrorMessage } from "../../../../helpers/helpers";
import { deleteNamespace } from "../../../../api/deleteNamespace";
import ObjectModal from "../../../Layout/LayoutComponents/FluxObjects/ObjectModal";
import { VectorMemoryModal } from "../../../UI/VectorMemoryModal";
import CreateFormModal from "../../../Forms/CreateFormModal";
import AddToLibraryModal from "../../../Library/AddToLibraryModal";
import { Folder, Flows } from "../../../../util/Types/Folder";
import { UserObject } from "../../../../util/Types/User";
import { Space, OrganizedItems } from "../../../../util/Types/Sidebar";
import { Namespace } from "../../../../types/namespace";
import {
  isFluxbotFlow,
  isFormFlow,
  parseSpaceIdFromFolderId,
  isVirtualFolderIdBasic,
} from "../utils";
import {
  AgentsSidebarContext,
  AgentsSidebarContextValue,
  FluxObject,
  useAgentsSidebarContext,
} from "./context";
import { useExtendedSidebarContext } from "../ExtendedSidebar";
import { Section } from "./Section";
import { FolderItem } from "./FolderItem";
import { FluxBotItem } from "./FluxBotItem";
import { Item } from "./Item";
import { ActionMenu } from "./ActionMenu";
import { BoundingBox, ChatText, FileText, Stack } from "@phosphor-icons/react";
import TemplateSystemModal from "../../../TemplateSystemModal/TemplateSystemModal";

const RootAgentItem: React.FC<{
  agent: Flows;
  folderId: string;
  forceFluxbotIcon?: boolean;
  onRename?: (flow: Flows) => void;
  onDelete?: (flow: Flows) => void;
  onShare?: (flow: Flows) => void;
  onOpenInNewTab?: (flow: Flows) => void;
  onCreateInterface?: (flow: Flows, folderId: string) => void;
  onAddToLibrary?: (flow: Flows, folderId: string) => void;
  onDuplicate?: (flow: Flows, folderId: string) => void;
}> = ({
  agent,
  folderId,
  forceFluxbotIcon = false,
  onRename,
  onDelete,
  onShare,
  onOpenInNewTab,
  onCreateInterface,
  onAddToLibrary,
  onDuplicate,
}) => {
  const { activeFlowId, onFlowClick } = useAgentsSidebarContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isForm = isFormFlow(agent);
  const isFluxbot = forceFluxbotIcon || isFluxbotFlow(agent);

  return (
    <>
      <Item
        icon={
          isForm ? (
            <FileText size={16} />
          ) : isFluxbot ? (
            <ChatText size={16} />
          ) : (
            <BoundingBox size={16} />
          )
        }
        label={agent.name}
        isActive={activeFlowId === agent.id}
        onClick={() => onFlowClick(agent, folderId)}
        onMenuClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onRename={onRename ? () => onRename(agent) : undefined}
        onShare={!isForm && onShare ? () => onShare(agent) : undefined}
        onOpenInNewTab={
          onOpenInNewTab ? () => onOpenInNewTab(agent) : undefined
        }
        onCreateInterface={
          !isForm && onCreateInterface
            ? () => onCreateInterface(agent, folderId)
            : undefined
        }
        onAddToLibrary={
          !isForm && onAddToLibrary
            ? () => onAddToLibrary(agent, folderId)
            : undefined
        }
        libraryLabel={
          agent.library ? "Edit Library" : "Add to Library"
        }
        onDuplicate={
          !isForm && onDuplicate
            ? () => onDuplicate(agent, folderId)
            : undefined
        }
        onDelete={onDelete ? () => onDelete(agent) : undefined}
      />
    </>
  );
};

const VectorMemoryItem: React.FC<{
  namespace: Namespace;
  onEdit: (ns: Namespace) => void;
  onDelete: (ns: Namespace) => void;
}> = ({ namespace, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Item
        icon={<FileText size={16} />}
        label={namespace.name}
        isNested
        onMenuClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget as HTMLElement);
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onEdit={() => {
          setAnchorEl(null);
          onEdit(namespace);
        }}
        onDelete={() => {
          setAnchorEl(null);
          onDelete(namespace);
        }}
        showEdit={true}
      />
    </>
  );
};

interface LocalOrganizedItems {
  agents: Flows[];
  fluxbots: Flows[];
  objects: FluxObject[];
  vectorMemory: Namespace[];
}

type FlowWithRootFolder = { flow: Flows; rootFolderName: string };

const extractAllFlowsFromFolder = (
  folder: Folder,
  rootFolderName?: string,
): FlowWithRootFolder[] => {
  const allFlows: FlowWithRootFolder[] = [];
  const currentRootName = rootFolderName || folder.name || "";

  if (folder.flows && folder.flows.length > 0) {
    folder.flows.forEach((flow) => {
      allFlows.push({ flow, rootFolderName: currentRootName });
    });
  }
  if (folder.folders && folder.folders.length > 0) {
    folder.folders.forEach((subfolder) => {
      const subfolderFlows = extractAllFlowsFromFolder(
        subfolder,
        currentRootName,
      );
      allFlows.push(...subfolderFlows);
    });
  }
  return allFlows;
};

const getAllFlowsFromAllFolders = (folders: Folder[]): FlowWithRootFolder[] => {
  const allFlows: FlowWithRootFolder[] = [];
  folders.forEach((folder) => {
    const folderFlows = extractAllFlowsFromFolder(folder);
    allFlows.push(...folderFlows);
  });
  return allFlows;
};

const organizeItemsByCategory = (
  folders: Folder[],
  flows: Flows[],
  objects: FluxObject[],
  namespaces: Namespace[],
): LocalOrganizedItems => {
  const result: LocalOrganizedItems = {
    agents: [],
    fluxbots: [],
    objects: objects || [],
    vectorMemory: namespaces || [],
  };

  const seenFlowIds = new Set<string>();
  const appendFlow = (flow: Flows, rootFolderName?: string) => {
    if (seenFlowIds.has(flow.id)) return;
    seenFlowIds.add(flow.id);
    if (isFluxbotFlow(flow, rootFolderName)) {
      result.fluxbots.push(flow);
      return;
    }
    if (isFormFlow(flow)) return;
    result.agents.push(flow);
  };

  getAllFlowsFromAllFolders(folders).forEach(({ flow, rootFolderName }) => {
    appendFlow(flow, rootFolderName);
  });

  (flows || []).forEach((flow) => {
    appendFlow(flow);
  });

  return result;
};

export const AgentsSidebarBody: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ flowId?: string }>();
  const {
    setFolders,
    folders: storeFolders,
    user: authUser,
    setAuthUser,
  } = useAuthStore((state) => state);
  const { searchTerm } = useExtendedSidebarContext();
  const {
    setObjectUpdate,
    resetObjectUpdate,
    removeObject,
    setObjects,
    objects: objectStore,
  } = useObjectStore((state) => state);
  const { setFlows, flows: storeFlows } = useFlowsStore((state) => state);
  const { removeFormTabWithNavigation } = useFormTabStore();
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );

  const {
    folders,
    spaces,
    user: sidebarUser,
    isLoading: sidebarLoading,
    refresh: refreshSidebar,
    updateFlow: updateFlowOptimistic,
    removeFlow: removeFlowOptimistic,
    updateSpace,
  } = useSidebarData();

  useEffect(() => {
    if (sidebarUser) {
      setAuthUser(sidebarUser);
    }
  }, [sidebarUser, setAuthUser]);

  useEffect(() => {
    if (folders.length > 0) {
      setFolders(folders);
    } else if (folders.length === 0 && storeFolders.length > 0) {
      setFolders([]);
    }
  }, [folders, setFolders, storeFolders.length]);

  useEffect(() => {
    const handleSidebarRefresh = () => {
      refreshSidebar();
    };

    window.addEventListener("sidebar-refresh", handleSidebarRefresh);
    return () => {
      window.removeEventListener("sidebar-refresh", handleSidebarRefresh);
    };
  }, [refreshSidebar]);

  const [activeFlowId, setActiveFlowId] = useState<string | undefined>();
  const [activeObjectId, setActiveObjectId] = useState<string | undefined>();
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showObjectModal, setShowObjectModal] = useState(false);
  const [showVectorModal, setShowVectorModal] = useState(false);
  const [editingObject, setEditingObject] = useState<FluxObject | null>(null);
  const [editingVectorMemory, setEditingVectorMemory] =
    useState<Namespace | null>(null);

  const getUserObjects = useCallback((): FluxObject[] => {
    return objectStore["user"] || authUser?.objects || [];
  }, [objectStore, authUser?.objects]);

  const getSpaceObjects = useCallback(
    (spaceId: string): FluxObject[] => {
      return objectStore[spaceId] || [];
    },
    [objectStore],
  );

  const fluxObjects: FluxObject[] = getUserObjects();

  useEffect(() => {
    if (
      params.flowId &&
      (location.pathname.includes("/agent/") ||
        location.pathname.includes("/spaceAgent/") ||
        location.pathname.includes("/interface/"))
    ) {
      setActiveFlowId(params.flowId);
    } else {
      setActiveFlowId(undefined);
    }
  }, [params.flowId, location.pathname]);

  const handleFlowClick = useCallback(
    (flow: Flows, folderId?: string) => {
      setActiveFlowId(flow.id);
      setActiveObjectId(undefined);

      const isForm = isFormFlow(flow);

      let spaceId: string | undefined;

      if (folderId) {
        const parsedSpaceId = parseSpaceIdFromFolderId(folderId);
        if (parsedSpaceId) {
          spaceId = parsedSpaceId;
        } else {
          const isSpaceId = spaces.some((space) => space.id === folderId);
          if (isSpaceId) {
            spaceId = folderId;
          } else {
            const folder =
              folders.find((f) => f.id === folderId) ||
              spaces
                .flatMap((s) => s.folder || [])
                .find((f: Folder) => f.id === folderId);
            if (folder?.space) {
              spaceId = folder.space;
            }
          }
        }
      }

      if (!spaceId) {
        const flowSpaceId =
          flow.spaceId ||
          (typeof flow.space === "string" ? flow.space : undefined);
        spaceId = flowSpaceId;
      }

      const isSpaceFlow = !!spaceId;
      let route: string;

      if (isForm) {
        route = isSpaceFlow
          ? `/dashboard-beta/interface/${flow.id}${
              spaceId ? `?spaceId=${spaceId}` : ""
            }`
          : `/dashboard-beta/interface/${flow.id}`;
      } else {
        route = isSpaceFlow
          ? `/dashboard-beta/spaceAgent/${flow.id}${
              spaceId ? `?spaceId=${spaceId}` : ""
            }`
          : `/dashboard-beta/agent/${flow.id}`;
      }

      navigate(route);
    },
    [navigate, spaces, folders],
  );

  const handleObjectClick = useCallback((obj: FluxObject) => {
    setActiveObjectId(obj.id);
    setActiveFlowId(undefined);
  }, []);

  const handleFlowRename = useCallback(
    async (flow: Flows) => {
      const newName = prompt("Enter new name:", flow.name);
      if (!newName || newName.trim() === flow.name) return;

      const trimmedName = newName.trim();
      updateFlowOptimistic(flow.id, { name: trimmedName });

      try {
        await FlowService.updateFlow(flow.id, { name: trimmedName });
        setNotification({
          type: NotificationType.Success,
          message: "Agent renamed successfully",
        });
      } catch (error) {
        refreshSidebar();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [setNotification, updateFlowOptimistic, refreshSidebar],
  );

  const handleFlowDelete = useCallback(
    async (flow: Flows) => {
      const isForm = isFormFlow(flow);
      const isFluxbot = flow.type === "chat";
      const itemType = isForm ? "interface" : isFluxbot ? "fluxbot" : "agent";

      if (
        !window.confirm(
          `Are you sure you want to delete this ${itemType}? This action cannot be undone.`,
        )
      ) {
        return;
      }

      try {
        removeFlowOptimistic(flow.id);
        setFlows(storeFlows.filter((f) => f.id !== flow.id));

        if (isForm) {
          await FormsService.deleteForm(flow.id);
          removeFormTabWithNavigation(flow.id, navigate);
        } else {
          const flowType = isFluxbot ? "chat" : undefined;
          await FlowService.deleteFlow(flow.id, flowType);
        }

        const successMessage = isForm
          ? "Interface deleted successfully"
          : isFluxbot
            ? "Fluxbot deleted successfully"
            : "Agent deleted successfully";

        setNotification({
          type: NotificationType.Success,
          message: successMessage,
        });
        if (activeFlowId === flow.id && !isForm) {
          navigate("/dashboard-beta");
        }
      } catch (error) {
        refreshSidebar();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [
      setNotification,
      activeFlowId,
      navigate,
      refreshSidebar,
      setFlows,
      storeFlows,
      removeFlowOptimistic,
      removeFormTabWithNavigation,
    ],
  );

  const handleFlowShare = useCallback(
    (flow: Flows) => {
      const isForm = isFormFlow(flow);
      const url = isForm
        ? `${window.location.origin}/interface/${flow.id}`
        : `${window.location.origin}/agent/${flow.id}`;
      navigator.clipboard.writeText(url);
      setNotification({
        type: NotificationType.Success,
        message: "Link copied to clipboard",
      });
    },
    [setNotification],
  );

  const handleFlowOpenInNewTab = useCallback((flow: Flows) => {
    const isForm = isFormFlow(flow);
    const url = isForm
      ? `/dashboard-beta/interface/${flow.id}`
      : `/dashboard-beta/agent/${flow.id}`;
    window.open(url, "_blank");
  }, []);

  const [createInterfaceFlow, setCreateInterfaceFlow] = useState<{
    flow: Flows;
    folderId: string;
  } | null>(null);
  const [isCreateFormLoading, setIsCreateFormLoading] = useState(false);

  const handleCreateInterface = useCallback((flow: Flows, folderId: string) => {
    setCreateInterfaceFlow({ flow, folderId });
  }, []);

  const handleCreateForm = useCallback(
    async (
      name: string,
      useAgent: boolean,
      flowId: string,
      spaceId?: string,
    ) => {
      setIsCreateFormLoading(true);
      try {
        const response = await FormsService.createForm({
          name,
          flowId,
          folderId: createInterfaceFlow?.folderId || "",
          index: 1,
          useAgent,
        });
        navigate(
          `/dashboard-beta/interface/${response.data.id}${
            spaceId ? `?spaceId=${spaceId}` : ""
          }`,
        );
        setCreateInterfaceFlow(null);
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: "Interface created successfully",
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      } finally {
        setIsCreateFormLoading(false);
      }
    },
    [createInterfaceFlow, navigate, refreshSidebar, setNotification],
  );

  const [libraryFlow, setLibraryFlow] = useState<{
    flow: Flows;
    folderId: string;
  } | null>(null);

  const handleAddToLibrary = useCallback((flow: Flows, folderId: string) => {
    setLibraryFlow({ flow, folderId });
  }, []);

  const handleFlowDuplicate = useCallback(
    async (flow: Flows, folderId: string) => {
      try {
        const realFolderId = isVirtualFolderIdBasic(folderId)
          ? undefined
          : folderId;

        const spaceId = parseSpaceIdFromFolderId(folderId);

        await FlowService.duplicateFlow({
          flowId: flow.id,
          folderId: realFolderId,
          spaceId,
        });

        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: "Agent duplicated successfully",
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [refreshSidebar, setNotification],
  );

  const handleObjectEdit = useCallback(
    (obj: FluxObject) => {
      const fullObject: UserObject | undefined =
        authUser?.objects?.find((o) => o.id === obj.id) ||
        (spaces
          .flatMap((s) => s.objects || [])
          .find((o) => o.id === obj.id) as UserObject | undefined);
      if (fullObject) {
        setObjectUpdate({
          isCreating: false,
          name: fullObject.name,
          id: fullObject.id,
          sessions: fullObject.sessions,
          text: fullObject.text,
          localObject: fullObject.localObject,
          sessionEnabled: fullObject.sessionEnabled,
          flowId: fullObject.flowId,
        });
        setEditingObject(fullObject);
        setShowObjectModal(true);
      }
    },
    [authUser, spaces, setObjectUpdate],
  );

  const handleObjectRename = useCallback(
    (obj: FluxObject) => {
      handleObjectEdit(obj);
    },
    [handleObjectEdit],
  );

  const handleObjectDelete = useCallback(
    async (obj: FluxObject) => {
      if (!window.confirm("Are you sure you want to delete this object?")) {
        return;
      }

      try {
        const spaceId = spaces.find((s) =>
          s.objects?.some((o) => o.id === obj.id),
        )?.id;
        let newObjects;
        const actualSpaceId = spaceId || "user";

        removeObject(obj.id, actualSpaceId);

        if (spaceId) {
          const space = spaces.find((s) => s.id === spaceId);
          newObjects = (space?.objects || []).filter(
            (o) => o.id !== obj.id,
          );
          await CompanySpacesService.updateCompanySpace(
            authUser?.selectedCompanyId!,
            {
              spaceId,
              objects: newObjects,
            },
          );
          setObjects(newObjects as UserObject[], spaceId);
        } else {
          newObjects = (authUser?.objects || []).filter(
            (o) => o.id !== obj.id,
          );
          await UserService.updateUser({ objects: newObjects });
          refreshSidebar();
          setObjects(newObjects, "user");
        }

        setNotification({
          type: NotificationType.Success,
          message: "Object deleted successfully",
        });
      } catch (error) {
        refreshSidebar();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [
      authUser,
      spaces,
      setNotification,
      refreshSidebar,
      setObjects,
      removeObject,
    ],
  );

  const handleVectorMemoryEdit = useCallback((ns: Namespace) => {
    setEditingVectorMemory(ns);
    setShowVectorModal(true);
  }, []);

  const handleVectorMemoryDelete = useCallback(
    async (ns: Namespace) => {
      if (
        !window.confirm("Are you sure you want to delete this vector memory?")
      ) {
        return;
      }

      try {
        const spaceId = spaces.find((s) =>
          s.namespaces?.some((n) => n.id === ns.id),
        )?.id;

        if (spaceId) {
          updateSpace(spaceId, (space) => ({
            ...space,
            namespaces:
              space.namespaces?.filter((n) => n.id !== ns.id) || [],
          }));
        }

        if (!spaceId) {
          refreshSidebar();
        }

        await deleteNamespace({
          body: {
            embeddingModelName: ns.embeddingModelName || "voyage-3-large",
          },
          params: {
            id: ns.id,
            nameSpace: ns.name,
            spaceId: spaceId,
          },
        });

        setNotification({
          type: NotificationType.Success,
          message: "Vector memory deleted successfully",
        });
      } catch (error) {
        refreshSidebar();
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [spaces, setNotification, refreshSidebar],
  );

  const handleFolderRename = useCallback(
    async (folder: Folder) => {
      const newName = prompt("Enter new name:", folder.name);
      if (!newName || newName.trim() === folder.name) return;

      try {
        await FolderService.updateFolder(folder.id, {
          name: newName.trim(),
        });
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: "Folder renamed successfully",
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  const handleFolderDelete = useCallback(
    async (folder: Folder) => {
      if (
        !window.confirm(
          "Are you sure you want to delete this folder? This action cannot be undone.",
        )
      ) {
        return;
      }

      try {
        await FolderService.deleteFolder(folder.id);
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: "Folder deleted successfully",
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  const handleAddFolder = useCallback(
    async (spaceId?: string) => {
      const name = prompt("Enter folder name:");
      if (!name || !name.trim()) return;

      try {
        if (spaceId) {
          await FolderService.createFolderOnSpace({
            name: name.trim(),
            spaceId,
          });
        } else {
          await FolderService.createFolder({ name: name.trim() });
        }
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: "Folder created successfully",
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  const handleEditSpace = useCallback(
    (spaceId: string) => {
      navigate(window.location.pathname + "?space=true&spaceId=" + spaceId);
      window.dispatchEvent(new CustomEvent("open-settings-modal"));
    },
    [navigate],
  );

  const handleArchiveSpace = useCallback(
    async (spaceId: string) => {
      if (!window.confirm("Are you sure you want to archive this space?")) {
        return;
      }

      try {
        await CompanySpacesService.disableSpace(spaceId);
        refreshSidebar();
        setNotification({
          type: NotificationType.Success,
          message: "Space archived successfully",
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }
    },
    [setNotification, refreshSidebar],
  );

  if (sidebarLoading && folders.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const contextValue: AgentsSidebarContextValue = {
    isDarkMode,
    palette,
    activeFlowId,
    activeObjectId,
    onFlowClick: handleFlowClick,
    onObjectClick: handleObjectClick,
  };

  const filterItems = (
    items: LocalOrganizedItems,
    searchTerm: string,
  ): LocalOrganizedItems => {
    if (!searchTerm.trim()) {
      return items;
    }
    const searchLower = searchTerm.toLowerCase();
    return {
      agents: items.agents.filter((flow) =>
        flow.name?.toLowerCase().includes(searchLower),
      ),
      fluxbots: items.fluxbots.filter((flow) =>
        flow.name?.toLowerCase().includes(searchLower),
      ),
      objects: items.objects.filter((obj) =>
        obj.name?.toLowerCase().includes(searchLower),
      ),
      vectorMemory: items.vectorMemory.filter((ns) =>
        ns.name?.toLowerCase().includes(searchLower),
      ),
    };
  };

  const createMainFolder = (
    name: string,
    items: LocalOrganizedItems,
    spaceId?: string,
  ): Folder => {
    let flows: Flows[] = [];
    if (name === "Agents") {
      flows = items.agents;
    } else if (name === "Fluxbots") {
      flows = items.fluxbots;
    }
    return {
      id: `${spaceId || "private"}-${name}`,
      name: name,
      flows: flows,
      index: 0,
      folders: [],
      flowsLength: flows.length,
      space: spaceId || "",
    };
  };

  const getAgentsFolderStructure = (
    folders: Folder[],
  ): { rootAgents: Flows[]; subfolders: Folder[] } => {
    const rootAgents: Flows[] = [];
    const subfolders: Folder[] = [];
    const seenFlows = new Set<string>();
    const seenSubfolders = new Set<string>();
    const skipFolderNames = new Set<string>([
      "agents",
      "chatbot",
      "fluxbots",
      "shared",
      "vector memory",
    ]);

    const traverse = (folderList: Folder[]) => {
      folderList.forEach((folder) => {
        const nameLower = folder.name?.trim().toLowerCase() || "";

        if (nameLower === "agents") {
          (folder.flows || []).forEach((flow) => {
            if (seenFlows.has(flow.id)) return;
            if (isFluxbotFlow(flow)) return;
            if (isFormFlow(flow)) return;
            seenFlows.add(flow.id);
            rootAgents.push(flow);
          });
          (folder.folders || []).forEach((subfolder) => {
            if (!seenSubfolders.has(subfolder.id)) {
              seenSubfolders.add(subfolder.id);
              subfolders.push(subfolder);
            }
          });
        } else if (!skipFolderNames.has(nameLower)) {
          if (!seenSubfolders.has(folder.id)) {
            seenSubfolders.add(folder.id);
            subfolders.push(folder);
          }
        }

        if (folder.folders && folder.folders.length > 0) {
          traverse(folder.folders);
        }
      });
    };

    traverse(folders);

    return { rootAgents, subfolders };
  };

  const privateItemsRaw = organizeItemsByCategory(
    folders,
    [],
    fluxObjects,
    (authUser?.namespaces as Namespace[]) || [],
  );

  const allSpacesItemsRaw = spaces.map((space) =>
    organizeItemsByCategory(
      space.folder || [],
      space.flows || [],
      getSpaceObjects(space.id).length > 0
        ? getSpaceObjects(space.id)
        : space.objects || [],
      space.namespaces || [],
    ),
  );

  const combineAllItems = (): LocalOrganizedItems => {
    const combined: LocalOrganizedItems = {
      agents: [...privateItemsRaw.agents],
      fluxbots: [...privateItemsRaw.fluxbots],
      objects: [...privateItemsRaw.objects],
      vectorMemory: [...privateItemsRaw.vectorMemory],
    };

    allSpacesItemsRaw.forEach((spaceItems) => {
      combined.agents.push(...spaceItems.agents);
      combined.fluxbots.push(...spaceItems.fluxbots);
      combined.objects.push(...spaceItems.objects);
      combined.vectorMemory.push(...spaceItems.vectorMemory);
    });

    return combined;
  };

  const hasSearchTerm = searchTerm.trim().length > 0;
  const allItems = hasSearchTerm
    ? filterItems(combineAllItems(), searchTerm)
    : null;

  return (
    <AgentsSidebarContext.Provider value={contextValue}>
      {showTemplatesModal && (
        <TemplateSystemModal onClose={() => setShowTemplatesModal(false)} />
      )}
      {showObjectModal && (
        <ObjectModal
          show={showObjectModal}
          onHide={async () => {
            setShowObjectModal(false);
            setEditingObject(null);
            resetObjectUpdate();
            refreshSidebar();
          }}
          spaceId={
            editingObject
              ? spaces.find((s) =>
                  s.objects?.some((o) => o.id === editingObject.id),
                )?.id || "user"
              : "user"
          }
        />
      )}
      {showVectorModal && (
        <VectorMemoryModal
          namespaceId={editingVectorMemory?.id}
          spaceId={
            editingVectorMemory
              ? spaces.find((s) =>
                  s.namespaces?.some(
                    (n) => n.id === editingVectorMemory.id,
                  ),
                )?.id
              : undefined
          }
          open={showVectorModal}
          onOpenChange={async (state) => {
            setShowVectorModal(state.open);
            if (!state.open) {
              setEditingVectorMemory(null);
              refreshSidebar();
            }
          }}
        />
      )}
      <Box
        sx={{
          padding: "8px",
          borderBottom: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.gray[200]
          }`,
          marginBottom: "8px",
        }}
      >
        <Item
          icon={<Stack size={16} />}
          label="Templates"
          onClick={() => setShowTemplatesModal(true)}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {hasSearchTerm ? (
          <>
            {(() => {
              const searchLower = searchTerm.toLowerCase();
              const agentsStructure = getAgentsFolderStructure(folders);
              const allSpacesAgentsStructure = spaces.flatMap((space) =>
                getAgentsFolderStructure(space.folder || []),
              );
              const allRootAgents = [
                ...agentsStructure.rootAgents,
                ...allSpacesAgentsStructure.flatMap((s) => s.rootAgents),
              ];
              const allSubfolders = [
                ...agentsStructure.subfolders,
                ...allSpacesAgentsStructure.flatMap((s) => s.subfolders),
              ];

              const folderHasMatchingContent = (folder: Folder): boolean => {
                const nameMatch = folder.name
                  ?.toLowerCase()
                  .includes(searchLower);
                const flowMatch = folder.flows?.some((flow) =>
                  flow.name?.toLowerCase().includes(searchLower),
                );
                const subfolderMatch = folder.folders?.some((sub) =>
                  folderHasMatchingContent(sub),
                );
                return !!(nameMatch || flowMatch || subfolderMatch);
              };

              const filteredSubfolders = allSubfolders.filter((subfolder) =>
                folderHasMatchingContent(subfolder),
              );

              const filteredRootAgents = allRootAgents.filter((agent) =>
                agent.name?.toLowerCase().includes(searchLower),
              );

              return (
                <>
                  {filteredSubfolders.map((subfolder) => (
                    <FolderItem
                      key={subfolder.id}
                      folder={subfolder}
                      onFlowRename={handleFlowRename}
                      onFlowDelete={handleFlowDelete}
                      onFlowShare={handleFlowShare}
                      onFlowOpenInNewTab={handleFlowOpenInNewTab}
                      onFlowCreateInterface={handleCreateInterface}
                      onFlowAddToLibrary={handleAddToLibrary}
                      onFlowDuplicate={handleFlowDuplicate}
                      onFolderRename={handleFolderRename}
                      onFolderDelete={handleFolderDelete}
                    />
                  ))}
                  {filteredRootAgents.map((agent) => (
                    <RootAgentItem
                      key={agent.id}
                      agent={agent}
                      folderId="search-Agents"
                      onRename={handleFlowRename}
                      onDelete={handleFlowDelete}
                      onShare={handleFlowShare}
                      onOpenInNewTab={handleFlowOpenInNewTab}
                      onCreateInterface={handleCreateInterface}
                      onAddToLibrary={handleAddToLibrary}
                      onDuplicate={handleFlowDuplicate}
                    />
                  ))}
                </>
              );
            })()}
            {allItems!.fluxbots.map((flow) => (
              <RootAgentItem
                key={flow.id}
                agent={flow}
                folderId="search-Fluxbots"
                forceFluxbotIcon={true}
                onRename={handleFlowRename}
                onDelete={handleFlowDelete}
                onShare={handleFlowShare}
                onOpenInNewTab={handleFlowOpenInNewTab}
                onCreateInterface={handleCreateInterface}
                onAddToLibrary={handleAddToLibrary}
                onDuplicate={handleFlowDuplicate}
              />
            ))}
            {allItems!.objects.map((obj: FluxObject) => (
              <FluxBotItem
                key={obj.id}
                object={obj}
                onEdit={handleObjectEdit}
                onRename={handleObjectRename}
                onDelete={handleObjectDelete}
                isNested={false}
              />
            ))}
            {allItems!.vectorMemory.length > 0 && (
                <FolderItem
                  folder={createMainFolder("Vector Memory", allItems!)}
                  customItems={allItems!.vectorMemory.map((ns) => (
                  <VectorMemoryItem
                    key={ns.id}
                    namespace={ns}
                    onEdit={handleVectorMemoryEdit}
                    onDelete={handleVectorMemoryDelete}
                  />
                ))}
              />
            )}
          </>
        ) : (
          <>
            <Section
              title="Private"
              hasBorder={spaces.length > 0}
              isAccordion={true}
              defaultExpanded={true}
              onAddFolder={() => handleAddFolder()}
            >
              {(() => {
                const agentsStructure = getAgentsFolderStructure(folders);
                return (
                  <>
                    {agentsStructure.subfolders.map((subfolder) => (
                      <FolderItem
                        key={subfolder.id}
                        folder={subfolder}
                        onFlowRename={handleFlowRename}
                        onFlowDelete={handleFlowDelete}
                        onFlowShare={handleFlowShare}
                        onFlowOpenInNewTab={handleFlowOpenInNewTab}
                        onFlowCreateInterface={handleCreateInterface}
                        onFlowAddToLibrary={handleAddToLibrary}
                        onFlowDuplicate={handleFlowDuplicate}
                        onFolderRename={handleFolderRename}
                        onFolderDelete={handleFolderDelete}
                      />
                    ))}
                    <FolderItem
                      folder={createMainFolder(
                        "Vector Memory",
                        privateItemsRaw,
                      )}
                      customItems={privateItemsRaw.vectorMemory.map(
                        (ns) => (
                          <VectorMemoryItem
                            key={ns.id}
                            namespace={ns}
                            onEdit={handleVectorMemoryEdit}
                            onDelete={handleVectorMemoryDelete}
                          />
                        ),
                      )}
                    />
                    {agentsStructure.rootAgents.map((agent) => (
                      <RootAgentItem
                        key={agent.id}
                        agent={agent}
                        folderId={`private-Agents`}
                        onRename={handleFlowRename}
                        onDelete={handleFlowDelete}
                        onShare={handleFlowShare}
                        onOpenInNewTab={handleFlowOpenInNewTab}
                        onCreateInterface={handleCreateInterface}
                        onAddToLibrary={handleAddToLibrary}
                        onDuplicate={handleFlowDuplicate}
                      />
                    ))}
                    {privateItemsRaw.fluxbots.map((flow) => (
                      <RootAgentItem
                        key={flow.id}
                        agent={flow}
                        folderId={`private-Fluxbots`}
                        forceFluxbotIcon={true}
                        onRename={handleFlowRename}
                        onDelete={handleFlowDelete}
                        onShare={handleFlowShare}
                        onOpenInNewTab={handleFlowOpenInNewTab}
                        onCreateInterface={handleCreateInterface}
                        onAddToLibrary={handleAddToLibrary}
                        onDuplicate={handleFlowDuplicate}
                      />
                    ))}
                    {privateItemsRaw.objects.map((obj: FluxObject) => (
                      <FluxBotItem
                        key={obj.id}
                        object={obj}
                        onEdit={handleObjectEdit}
                        onRename={handleObjectRename}
                        onDelete={handleObjectDelete}
                        isNested={false}
                      />
                    ))}
                  </>
                );
              })()}
            </Section>

            {[...spaces]
              .map((space, index) => ({
                space,
                spaceItems: allSpacesItemsRaw[index],
              }))
              .sort((a, b) => {
                const aDisabled = a.space.disabled ? 1 : 0;
                const bDisabled = b.space.disabled ? 1 : 0;
                return aDisabled - bDisabled;
              })
              .map(({ space, spaceItems }, index, sorted) => {
                return (
                  <Section
                    key={space.id}
                    title={space.name}
                    hasBorder={index < sorted.length - 1}
                    isAccordion={true}
                    defaultExpanded={false}
                    isDisabled={!!space.disabled}
                    onAddFolder={() => handleAddFolder(space.id)}
                    onEditSpace={() => handleEditSpace(space.id)}
                    onArchive={() => handleArchiveSpace(space.id)}
                  >
                    {(() => {
                      const agentsStructure = getAgentsFolderStructure(
                        space.folder || [],
                      );
                      return (
                        <>
                          {agentsStructure.subfolders.map((subfolder) => (
                            <FolderItem
                              key={subfolder.id}
                              folder={subfolder}
                              onFlowRename={handleFlowRename}
                              onFlowDelete={handleFlowDelete}
                              onFlowShare={handleFlowShare}
                              onFlowOpenInNewTab={handleFlowOpenInNewTab}
                              onFlowCreateInterface={handleCreateInterface}
                              onFlowAddToLibrary={handleAddToLibrary}
                              onFlowDuplicate={handleFlowDuplicate}
                              onFolderRename={handleFolderRename}
                              onFolderDelete={handleFolderDelete}
                            />
                          ))}
                          <FolderItem
                            folder={createMainFolder(
                              "Vector Memory",
                              spaceItems,
                              space.id,
                            )}
                            customItems={spaceItems.vectorMemory.map(
                              (ns) => (
                                <VectorMemoryItem
                                  key={ns.id}
                                  namespace={ns}
                                  onEdit={handleVectorMemoryEdit}
                                  onDelete={handleVectorMemoryDelete}
                                />
                              ),
                            )}
                          />
                          {agentsStructure.rootAgents.map((agent) => (
                            <RootAgentItem
                              key={agent.id}
                              agent={agent}
                              folderId={`${space.id}-Agents`}
                              onRename={handleFlowRename}
                              onDelete={handleFlowDelete}
                              onShare={handleFlowShare}
                              onOpenInNewTab={handleFlowOpenInNewTab}
                              onCreateInterface={handleCreateInterface}
                              onAddToLibrary={handleAddToLibrary}
                              onDuplicate={handleFlowDuplicate}
                            />
                          ))}
                          {spaceItems.fluxbots.map((flow) => (
                            <RootAgentItem
                              key={flow.id}
                              agent={flow}
                              folderId={`${space.id}-Fluxbots`}
                              forceFluxbotIcon={true}
                              onRename={handleFlowRename}
                              onDelete={handleFlowDelete}
                              onShare={handleFlowShare}
                              onOpenInNewTab={handleFlowOpenInNewTab}
                              onCreateInterface={handleCreateInterface}
                              onAddToLibrary={handleAddToLibrary}
                              onDuplicate={handleFlowDuplicate}
                            />
                          ))}
                          {spaceItems.objects.map((obj: FluxObject) => (
                            <FluxBotItem
                              key={obj.id}
                              object={obj}
                              onEdit={handleObjectEdit}
                              onRename={handleObjectRename}
                              onDelete={handleObjectDelete}
                              isNested={false}
                            />
                          ))}
                        </>
                      );
                    })()}
                  </Section>
                );
              })}
          </>
        )}
      </Box>

      <CreateFormModal
        flow={createInterfaceFlow?.flow}
        isCreateFormLoading={isCreateFormLoading}
        show={!!createInterfaceFlow}
        onCreateForm={handleCreateForm}
        onHide={() => setCreateInterfaceFlow(null)}
      />

      <AddToLibraryModal
        flow={libraryFlow?.flow}
        folderId={libraryFlow?.folderId}
        show={!!libraryFlow}
        onHide={() => setLibraryFlow(null)}
        isEditMode={!!libraryFlow?.flow.library}
        onAddToLibrary={() => {
          setNotification({
            type: NotificationType.Success,
            message: libraryFlow?.flow.library
              ? "Library updated successfully"
              : "Agent added to library successfully",
          });
          setLibraryFlow(null);
          refreshSidebar();
        }}
      />
    </AgentsSidebarContext.Provider>
  );
};
